import type { RouteLocationMatched, RouteLocationNormalizedGeneric, RouteLocationResolvedGeneric, RouteParamsGeneric } from 'vue-router';
import { RouteParams, type IRouteParams } from './RouteParams';
import { Route } from './Route';
import { useRouterStore } from '@/stores/routerStore';
import router from '@/router';

/**
 * The different routes we have in the application
 */
const RouteNameValues = [
    'home',
    'about',
    'series',
    'support',
    'notifications',
    'notauthorized',
    'eventinfo',
    'contestants',
    'categories',
    'groups',
    // 'stations',
    // 'stationroles',
    'bibanassign',
    'races',
    'quickinput',
    'matrix',
    'gpioreadings',
    'eventgraphics',
    'tracking',
    'eventdevicestatus',
    'production',
    'productionregister',
    'devicestatus',
    'trackdevices',
    'users',
    'approvecommunication',
    'dashboard',
    'racesetup',
    'traininglanes',
    'speaker',
];

export type RouteName = (typeof RouteNameValues)[number];

/**
 * Wrapper class for the router
 * This will help is navigate to different routes
 */
export class Router {
    /**
     * Helper property to keep the state of the dialogmodal
     */
    public static get HasOpenModal() {
        return this.OpenModals.size > 0;
    }

    private static OpenModals = new Set<Symbol>();

    /**
     * This is the latest exception that was thrown if the route didnt validate
     */
    public static Exception: Error | null = null;

    public static CurrentMatchedRoute: RouteLocationMatched | null = null;

    public static AddOpenModal(modalKey: Symbol) {
        Router.OpenModals.add(modalKey);
    }

    public static RemoveOpenModal(modalKey: Symbol) {
        Router.OpenModals.delete(modalKey);
    }

    /**
     * Helper function to validate the route
     * This will try and match the current route with the matched route and parse all the params
     * If the route is not found it will push the user to the notfound page
     */
    private static validateRoute(nextRoute: RouteLocationNormalizedGeneric) {
        try {
            const match = nextRoute.matched[0];

            if (!match) {
                throw new Error('Route not found');
            }

            // The real route in the app that it can use
            const matchedPattern = match.path?.split('/');

            // The path we are to go to. This is the path that is in the url
            const path = nextRoute.path?.split('/');

            // If the number of allowed parameters in the match is less than the number of parameters in the path we have a problem
            if (matchedPattern.length < path.length) {
                throw new Error(`Route not found.`);
            }

            // Now we loop throught the matchedPattern and path and replace the :id with the id from the path
            matchedPattern.forEach((item, index) => {
                // We need to check if it is optional
                const isOptional = item.includes('?');
                // We need to find the regexp part that is between ( ) and grab that value
                let regex = null;
                if (item.match(/\((.*?)\)/)) {
                    regex = item.match(/\((.*?)\)/)![1];
                }

                if (item.startsWith(':')) {
                    // First we check if the path has a value
                    if (path[index] != undefined) {
                        // This mean it is a variable so we check if it has a regex
                        if (regex) {
                            // We need to check if the regex is in the path
                            const re = new RegExp(regex, 'i');
                            if (!re.test(path[index])) {
                                throw new Error(`Route not found. ${path[index]} does not match ${regex}`);
                            }
                        } else {
                            // We assume it has to be a string and it cant be empty
                            if (path[index] === '') {
                                throw new Error(`Route param at index ${index} is empty`);
                            }
                        }
                    } else {
                        if (!isOptional) {
                            throw new Error(`Params ${item} is missing in the path and is mandatory`);
                        }
                    }
                } else {
                    // Need exact match
                    if (matchedPattern[index] !== path[index]) {
                        throw new Error(`Route not found. ${matchedPattern[index]} does not match ${path[index]}`);
                    }
                }
            });
        } catch (e) {
            // We keep the last exception that was thrown so we can show it.
            Router.Exception = e as Error;

            throw e;
        }
    }

    /**
     * Update the current route
     */
    public static UpdateRoute(nextRoute: RouteLocationNormalizedGeneric) {
        this.validateRoute(nextRoute);

        const route = new Route(String(nextRoute.name), nextRoute.fullPath, new RouteParams(nextRoute.params));

        if (JSON.stringify(useRouterStore().CurrentRoute) === JSON.stringify(route)) {
            throw new Error('Route is the same as current route', { cause: 'IdenticalRouteException' });
        }

        if (nextRoute.matched?.length > 0) {
            Router.CurrentMatchedRoute = nextRoute.matched[0];
            useRouterStore().setRoute(route);
        }
    }

    /**
     * Get the current route
     */
    public static get CurrentRoute(): Route {
        const current = useRouterStore().CurrentRoute;
        if (!current) {
            throw new Error('CurrentRoute is not set');
        }
        return new Route(current.name, current.href, new RouteParams(current.params));
    }

    /**
     * Get the current route root
     * @remarks This is based on the format /:eventID?/view/:tab?/:id?/:subtab? and will return everything before the :tab?
     */
    public static get CurrentRouteRoot(): string | null {
        const matched = this.CurrentMatchedRoute;

        const currentParams = this.RouteParams;

        if (!matched || !currentParams) {
            return null;
        }

        let path = matched.path;

        // We replace all parenthis maches ( ) and the content with empty strings
        path = path.replace(/\(.*?\)/g, '');

        for (const [key, value] of Object.entries(currentParams)) {
            const re = new RegExp(`:${key}?`, 'gi');
            path = path.replace(re, value);
        }

        // We split on / remove all items with a ? (optional) and then join it back together
        path = path
            .split('/')
            .filter((x) => !x.includes('?'))
            .join('/');

        return path;
    }

    /**
     * Navigate to a new route
     * @param params the parameters we want to navigate to
     * @param route the route we want to navigate to. If the route is not provided we use the current route and merge the params with the current params so we only need to provide the params we want to change
     */
    public static async NavigateTo(params: IRouteParams, route?: RouteLocationMatched | RouteName) {
        // if we dont provide a route we merge the current with the new params. That way we only need to provide the params we want to change
        if (!route) {
            params = { ...this.RouteParams, ...params };
        }

        // If we have a open modal, but no detail id is provided we will NOT navigate and change the route
        // This allows us to have independent modals without navigation open with TabComponent inside
        if (Router.HasOpenModal && !params.id) {
            return;
        }

        await router.push({
            name: (typeof route === 'string' ? route : route?.name) ?? this.CurrentMatchedRoute?.name,
            params: params as RouteParamsGeneric,
            force: true,
        });
    }

    /**
     *
     * @param path
     * @returns the route from the path or null if the route is not found
     */
    public static GetRouteFromPath(path: string) {
        const resolved: RouteLocationResolvedGeneric = router.resolve(path);

        if (resolved.matched.length < 1) {
            return null;
        }

        return new Route(String(resolved.name!), resolved.href!, new RouteParams(resolved.params));
    }

    /**
     * Get the route from the name and the parameters
     * @param name
     * @param params
     * @returns
     */
    public static GetRouteFromNameAndParams(name: RouteName, params: IRouteParams) {
        try {
            const resolved = router.resolve({ name: String(name), params: <RouteParamsGeneric>params });

            if (resolved.matched.length < 1) {
                return null;
            }

            return new Route(String(resolved.name!), resolved.href!, new RouteParams(resolved.params));
        } catch (e) {
            console.error(`Error resolving route ${name} with params ${JSON.stringify(params)}`, e);
            return null;
        }
    }

    /**
     * Get the active tab based on the route
     */
    public static get ActiveTab(): string | null {
        const params = this.RouteParams;
        return params?.subtab ?? params?.tab ?? null;
    }

    /**
     * Get the current route parameters
     * @returns the current route parameters as a typed object
     */
    public static get RouteParams(): IRouteParams {
        return new RouteParams(router?.currentRoute?.value?.params);
    }
}
